import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { dispatchSnackbar, downloader } from 'utils/helpers'
import { trackEvent } from 'utils/tracking'
import { CHURN_ZERO_EVENTS as cz } from 'utils/constants'

export const useUploadDownloadCsv = ({ useGetCsvLazyQuery, useUploadCsvMutation, fileType }) => {
  const dispatch = useDispatch()
  const today = new Date()

  const { organization, selectedYard } = useAppSelector(state => state.auth)

  const [
    getCsvData,
    { data: csvData, isLoading: isLoadingGetCsvData, requestId, isError, error: getCsvDataError, isSuccess },
  ] = useGetCsvLazyQuery()

  const [uploadCsv, { isLoading: isLoadingUploadCsv }] = useUploadCsvMutation()

  useEffect(() => {
    if (!csvData) return
    downloader(
      csvData,
      'text/csv',
      `${organization}-${selectedYard}-${today.toISOString().split('T')[0]}-${fileType}.csv`,
    )
  }, [csvData, requestId])

  const handleSuccess = isUpload => {
    trackEvent(isUpload ? cz.UPLOAD_CSV : cz.DOWNLOAD_CSV)
    dispatchSnackbar(dispatch, 'success', `CSV file ${isUpload ? 'uploaded' : 'downloaded'} successfully`)
  }

  const handleError = (error, isUpload) => {
    const errorList = error?.data?.errorsMessage || []
    const errorListString = errorList.join(', ')
    const errorMsg = errorList?.length
      ? `CSV ${isUpload ? 'upload' : 'download'} failed.${isUpload ? ` Please update the following errors and try again: ${errorListString}` : ''}`
      : error?.data?.error || `There was an error ${isUpload ? 'uploading' : 'downloading'} the CSV file`
    trackEvent({
      eventName: isUpload ? cz.UPLOAD_CSV : cz.DOWNLOAD_CSV,
      errorMsg,
    })
    dispatchSnackbar(dispatch, 'error', errorMsg)
  }

  useEffect(() => {
    if (isSuccess) {
      handleSuccess(false)
    }
    if (isError) {
      handleError(getCsvDataError, false)
    }
  }, [isError, isSuccess, requestId, getCsvDataError])

  const uploaderBtnRef = useRef<HTMLInputElement | null>(null)

  const submitCsvFile = async event => {
    const selectedCsv = event.target.files[0]
    if (!selectedCsv) return

    const file = new FormData()
    file.append('data', selectedCsv, 'priceSheetFile.csv')
    await uploadCsv({ body: file })
      .unwrap()
      .then(() => {
        handleSuccess(true)
      })
      .catch(err => {
        handleError(err, true)
      })
    // clear upload input after submit
    if (uploaderBtnRef.current) {
      uploaderBtnRef.current.value = ''
    }
  }

  return {
    isLoading: isLoadingGetCsvData || isLoadingUploadCsv,
    handleUploadButtonClick: () => {
      uploaderBtnRef?.current?.click()
    },
    handleDownloadButtonClick: () => {
      getCsvData()
    },
    renderFileUploadInput: () => (
      <input
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        ref={uploaderBtnRef}
        onChange={e => {
          if (e.target.value) {
            submitCsvFile(e)
          }
        }}
      />
    ),
  }
}
